// feedbackService.js — wrapper API del modulo. axios INIETTATO (niente @/axiosInstance,
// niente VUE_APP_API_URL). Endpoint pannello su /feedback/panel* (+ alias /admin legacy).
import { getClientLogsSnapshot } from "./clientLogService";

let apiClient = null;
// Iniettato una volta da installFeedbackModule({ axiosInstance }).
export const _setApiClient = (instance) => {
  apiClient = instance;
};
const client = () => {
  if (!apiClient)
    throw new Error(
      "[feedback] axiosInstance non inizializzato: chiama installFeedbackModule({ axiosInstance })."
    );
  return apiClient;
};

// ── Osservabilità [sc-47909] ───────────────────────────────────────────────
// Anche questi sono INIETTATI (come axios): il modulo resta portabile e non
// importa nulla dal CRM host (niente @/utilities/*). Se l'host non li fornisce,
// tutto degrada a no-op e il ticket parte comunque.
let observabilityProvider = null;
// Fornisce { lt_correlation_id, appId, release, glitchtipEventId, umamiWebsiteId }.
export const _setObservabilityProvider = (fn) => {
  observabilityProvider = typeof fn === "function" ? fn : null;
};
let tracker = null;
// Riceve (eventName, data) per gli eventi analytics del widget assistenza.
export const _setTracker = (fn) => {
  tracker = typeof fn === "function" ? fn : null;
};

// Nessuna delle due deve MAI far fallire l'invio di un ticket.
// Il provider può essere SYNC o ASYNC (sc-47917: glitchtipEventId richiede un
// flush async): l'await su un valore non-promise lo restituisce invariato, quindi
// entrambe le forme sono supportate senza rompere la compatibilità.
const observability = async () => {
  try {
    if (!observabilityProvider) return null;
    return (await observabilityProvider()) || null;
  } catch {
    return null;
  }
};
const track = (name, data) => {
  try {
    if (tracker) tracker(name, data);
  } catch {
    /* analytics best-effort */
  }
};

// Path pulito: via query string e fragment (possono contenere token/redirect/PII).
const cleanPath = (value) => {
  const s = typeof value === "string" ? value : "";
  const i = s.search(/[?#]/);
  return i === -1 ? s : s.slice(0, i);
};

// Allega i campi tecnici di correlazione alla FormData del ticket.
// - scalari TOP-LEVEL: è la forma che central persiste in colonne dedicate;
// - context/umamiContext: fallback transitorio, utile finché il relay API non
//   propaga i top-level (e comunque ridondanza a costo ~zero).
// I valori assenti vengono OMESSI: mai stringhe "undefined"/"null" a central.
async function appendObservability(fd) {
  const obs = await observability();
  if (!obs) return;
  const id = obs.lt_correlation_id || "";

  const scalars = {
    lt_correlation_id: id,
    appId: obs.appId,
    release: obs.release,
    glitchtipEventId: obs.glitchtipEventId,
    umamiWebsiteId: obs.umamiWebsiteId,
  };
  for (const [k, v] of Object.entries(scalars)) {
    if (v) fd.append(k, String(v));
  }

  // JSON.stringify omette da solo le chiavi undefined → niente valori fantasma.
  const observabilityCtx = {
    lt_correlation_id: id || undefined,
    appId: obs.appId || undefined,
    release: obs.release || undefined,
    glitchtipEventId: obs.glitchtipEventId || undefined,
    umamiWebsiteId: obs.umamiWebsiteId || undefined,
  };
  if (Object.values(observabilityCtx).some(Boolean)) {
    fd.append("context", JSON.stringify({ observability: observabilityCtx }));
  }
  if (id || obs.umamiWebsiteId) {
    fd.append(
      "umamiContext",
      JSON.stringify({
        lt_correlation_id: id || undefined,
        websiteId: obs.umamiWebsiteId || undefined,
      })
    );
  }
}

export const FEEDBACK_CATEGORIES = [
  { value: "bug", label: "Bug", icon: "🐛" },
  { value: "feature_request", label: "Feature request", icon: "✨" },
  { value: "ui_ux", label: "UI/UX", icon: "🎨" },
  { value: "other", label: "Altro", icon: "📝" },
];
export const categoryMeta = (value) =>
  FEEDBACK_CATEGORIES.find((c) => c.value === value) || {
    value,
    label: value,
    icon: "",
  };

// URL assoluto allegato: gestisce URL già assoluti; origin derivato dal baseURL axios.
export const imageUrl = (relative) => {
  try {
    if (/^https?:\/\//i.test(relative)) return relative;
    const base = apiClient?.defaults?.baseURL || "";
    const fallbackOrigin =
      typeof window !== "undefined"
        ? window.location.origin
        : "http://localhost";
    const cleanRelative = String(relative || "").startsWith("/")
      ? String(relative)
      : "/" + String(relative || "");
    return new URL(base, fallbackOrigin).origin + cleanRelative;
  } catch {
    return relative;
  }
};

export const STATUS_LABELS = {
  user: {
    waiting_admin: "In attesa del team",
    waiting_user: "Tocca a te",
    resolved: "Risolto",
    archived: "Archiviato",
  },
  admin: {
    waiting_admin: "Da rispondere",
    waiting_user: "In attesa utente",
    resolved: "Risolto",
    archived: "Archiviato",
  },
};
export const statusLabel = (status, role = "user") =>
  (STATUS_LABELS[role] || STATUS_LABELS.user)[status] || status;
export const statusTone = (status) =>
  status === "resolved"
    ? "tw-bg-emerald-100 tw-text-emerald-700"
    : status === "waiting_admin"
    ? "tw-bg-amber-100 tw-text-amber-700"
    : status === "waiting_user"
    ? "tw-bg-blue-100 tw-text-blue-700"
    : "tw-bg-slate-100 tw-text-slate-600";

async function messageForm(body, files = []) {
  const fd = new FormData();
  fd.append("body", body || "");
  for (const f of files) fd.append("screenshots", f);
  await appendObservability(fd);
  return fd;
}
const multipart = { headers: { "Content-Type": "multipart/form-data" } };

export default {
  // ── Utente ──
  async create({ category, text, pageUrl, routePath, files = [] }) {
    const fd = new FormData();
    fd.append("category", category);
    fd.append("text", text);
    // pageUrl/routePath SENZA query string né fragment: possono contenere
    // ?redirect=... o token. Il relay ripulisce comunque, ma il dato non deve
    // uscire sporco dal browser.
    const path = cleanPath(pageUrl);
    fd.append("pageUrl", path);
    fd.append("routePath", cleanPath(routePath) || path);
    for (const f of files) fd.append("screenshots", f);
    try {
      const logs = getClientLogsSnapshot();
      if (logs && logs.length) fd.append("clientLogs", JSON.stringify(logs));
    } catch {
      /* i log non bloccano mai */
    }
    await appendObservability(fd);

    const res = await client().post("/feedback", fd, multipart);
    // Solo dopo esito positivo. Nessuna PII: né testo, né allegati, né utente.
    track("support_ticket_submitted", { category });
    return res;
  },
  mine() {
    return client().get("/feedback/mine");
  },
  getTicket(id) {
    return client().get(`/feedback/${id}`);
  },
  async addMessage(id, { body, files = [] }) {
    const res = await client().post(
      `/feedback/${id}/messages`,
      await messageForm(body, files),
      multipart
    );
    track("support_message_submitted", {});
    return res;
  },
  markSeen() {
    return client().post("/feedback/seen");
  },

  // ── Pannello Assistenza (dev/support) ──
  panelList(params = {}) {
    return client().get("/feedback/panel", { params });
  },
  panelGetTicket(id) {
    return client().get(`/feedback/panel/${id}`);
  },
  async panelAddMessage(id, { body, files = [] }) {
    return client().post(
      `/feedback/panel/${id}/messages`,
      await messageForm(body, files),
      multipart
    );
  },
  panelUpdate(id, payload) {
    return client().patch(`/feedback/${id}`, payload);
  },

  // ── Alias legacy deprecati (mappati sul pannello) ──
  adminList(params = {}) {
    return this.panelList(params);
  },
  adminGetTicket(id) {
    return this.panelGetTicket(id);
  },
  adminAddMessage(id, payload) {
    return this.panelAddMessage(id, payload);
  },
  adminUpdate(id, payload) {
    return this.panelUpdate(id, payload);
  },
};
